/**
 * Stores Queries Service - TanStack Query Integration
 *
 * Este servicio encapsula todas las queries relacionadas con stores
 * (locations con type = STORE) usando TanStack Query.
 *
 * Características:
 * - Cache automático con staleTime y gcTime configurables
 * - Reintentos automáticos en caso de error
 * - Invalidación de cache inteligente
 * - Signals reactivos para estado de loading/error/data
 *
 * @see https://tanstack.com/query/latest/docs/framework/angular/overview
 */

import { Injectable, Signal, inject } from '@angular/core';
import {
  injectQuery,
  injectMutation,
  injectQueryClient,
  CreateQueryResult,
  CreateMutationResult,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

// Services
import { LocationQueriesApiService } from '../services/location-queries-api.service';

// Models
import {
  LocationModel,
  LocationFilterModel,
  LocationListModel,
  LocationCreateModel,
  BackendSchedule,
  OperatingHours,
  DayOperationHours,
} from '@kanzi-apes/kanzi-models';

// Query Keys
import { locationsQueryKeys, buildStoreFilters } from './locations-query-keys';

@Injectable({
  providedIn: 'root',
})
export class StoresQueriesService {
  private apiService = inject(LocationQueriesApiService);
  private queryClient = injectQueryClient();

  // ==================== PRIVATE HELPERS ====================

  /**
   * Maps backend schedules array to operating_hours format
   * Converts from array format to day-keyed object format
   */
  private mapSchedulesToOperatingHours(schedules: BackendSchedule[]): OperatingHours {
    if (!schedules || schedules.length === 0) {
      return {};
    }

    const dayMap: { [key: number]: keyof OperatingHours } = {
      0: 'monday',
      1: 'tuesday',
      2: 'wednesday',
      3: 'thursday',
      4: 'friday',
      5: 'saturday',
      6: 'sunday',
    };

    const operatingHours: OperatingHours = {};

    schedules.forEach((schedule) => {
      const dayKey = dayMap[schedule.day_of_week];
      if (dayKey && !schedule.is_closed) {
        operatingHours[dayKey] = {
          open_time: schedule.open_time.substring(0, 5), // "09:00:00" → "09:00"
          close_time: schedule.close_time.substring(0, 5), // "21:00:00" → "21:00"
        };
      }
    });

    return operatingHours;
  }

  /**
   * Processes location data to map schedules to operating_hours
   */
  private processLocationData(location: LocationModel): LocationModel {
    if (location.schedules && location.schedules.length > 0) {
      location.operating_hours = this.mapSchedulesToOperatingHours(location.schedules);
    }
    return location;
  }

  // ==================== QUERIES ====================

  /**
   * Query para listar Stores (locations con type = STORE)
   *
   * @param filters - Signal con filtros de búsqueda
   * @param enabled - Signal opcional que indica si la query está habilitada (default: true)
   * @returns CreateQueryResult con lista de stores
   *
   * @example
   * ```typescript
   * filters = signal<LocationFilterModel | null>({ page: 1, page_size: 50 });
   * isEnabled = signal(true);
   * storesQuery = this.queries.useStoresList(this.filters, this.isEnabled);
   *
   * // En template:
   * @if (storesQuery.isPending()) {
   *   <p-progressSpinner />
   * } @else if (storesQuery.data(); as stores) {
   *   <p-dataView [value]="stores.results"></p-dataView>
   * }
   * ```
   */
  useStoresList(
    filters: Signal<LocationFilterModel | null>,
    enabled?: Signal<boolean>
  ): CreateQueryResult<LocationListModel, Error> {
    return injectQuery(() => ({
      queryKey: locationsQueryKeys.storesList(filters()),
      queryFn: async () => {
        // Construir filtros con type = STORE
        const storeFilters = buildStoreFilters(filters());

        const result = await lastValueFrom(
          this.apiService.getLocationsList(storeFilters)
        );

        // Map schedules to operating_hours for each location
        if (result.results) {
          result.results = result.results.map((location) =>
            this.processLocationData(location)
          );
        }

        return result;
      },
      enabled: enabled ? enabled() : true,
      staleTime: 1000 * 60 * 5, // 5 minutos
      gcTime: 1000 * 60 * 30,   // 30 minutos
      retry: 1,
    }));
  }

  /**
   * Query para obtener detalle de un Store
   *
   * @param storeId - Signal con el ID del store
   * @param enabled - Signal que indica si la query está habilitada
   * @returns CreateQueryResult con detalle del store
   *
   * @example
   * ```typescript
   * selectedStoreId = signal<number | null>(null);
   * isDetailEnabled = signal(false);
   *
   * storeQuery = this.queries.useStoreDetail(
   *   this.selectedStoreId,
   *   this.isDetailEnabled
   * );
   * ```
   */
  useStoreDetail(
    storeId: Signal<number | null>,
    enabled: Signal<boolean>
  ): CreateQueryResult<LocationModel, Error> {
    return injectQuery(() => ({
      queryKey: storeId()
        ? locationsQueryKeys.storeDetail(storeId()!)
        : ['disabled'],
      queryFn: async () => {
        const id = storeId();
        if (!id) {
          throw new Error('Store ID is required');
        }
        const location = await lastValueFrom(
          this.apiService.getLocationDetail(id)
        );
        return this.processLocationData(location);
      },
      enabled: enabled() && storeId() !== null,
      staleTime: 1000 * 60 * 2, // 2 minutos
      gcTime: 1000 * 60 * 30,
      retry: 1,
    }));
  }

  // ==================== MUTATIONS ====================

  /**
   * Mutation para crear un nuevo Store
   *
   * @returns CreateMutationResult con funciones para ejecutar la mutación
   *
   * @example
   * ```typescript
   * createMutation = this.queries.useCreateStore();
   *
   * onCreateStore(store: LocationCreateModel) {
   *   this.createMutation.mutate(store, {
   *     onSuccess: (data) => {
   *       this.messageService.add({
   *         severity: 'success',
   *         summary: 'Tienda creada',
   *         detail: `Tienda ${data.name} creada exitosamente`
   *       });
   *       this.router.navigate(['stores', 'detail', data.id]);
   *     },
   *     onError: (error) => {
   *       console.error('Error creating store:', error);
   *     }
   *   });
   * }
   * ```
   */
  useCreateStore(): CreateMutationResult<
    LocationModel,
    Error,
    LocationCreateModel,
    unknown
  > {
    return injectMutation(() => ({
      mutationFn: async (store: LocationCreateModel) => {
        return lastValueFrom(this.apiService.createLocation(store));
      },
      onSuccess: () => {
        // Invalidar lista de stores para recargar
        this.queryClient.invalidateQueries({
          queryKey: locationsQueryKeys.stores(),
        });
      },
    }));
  }

  /**
   * Mutation para actualizar un Store existente
   *
   * @returns CreateMutationResult con funciones para ejecutar la mutación
   *
   * @example
   * ```typescript
   * updateMutation = this.queries.useUpdateStore();
   *
   * onUpdateStore(store: LocationCreateModel) {
   *   this.updateMutation.mutate(store);
   * }
   * ```
   */
  useUpdateStore(): CreateMutationResult<
    LocationModel,
    Error,
    LocationCreateModel,
    unknown
  > {
    return injectMutation(() => ({
      mutationFn: async (store: LocationCreateModel) => {
        if (!store.id) {
          throw new Error('Store ID is required for update');
        }
        return lastValueFrom(
          this.apiService.updateLocation(store)
        );
      },
      onSuccess: (data) => {
        // Invalidar lista y detalle
        this.queryClient.invalidateQueries({
          queryKey: locationsQueryKeys.stores(),
        });
        this.queryClient.invalidateQueries({
          queryKey: locationsQueryKeys.storeDetail(data.id!),
        });
      },
    }));
  }

  /**
   * Mutation para eliminar un Store
   *
   * @returns CreateMutationResult con funciones para ejecutar la mutación
   *
   * @example
   * ```typescript
   * deleteMutation = this.queries.useDeleteStore();
   *
   * onDeleteStore(storeId: number) {
   *   this.deleteMutation.mutate(storeId, {
   *     onSuccess: () => {
   *       this.router.navigate(['stores', 'list']);
   *     }
   *   });
   * }
   * ```
   */
  useDeleteStore(): CreateMutationResult<LocationModel, Error, number, unknown> {
    return injectMutation(() => ({
      mutationFn: async (storeId: number) => {
        return lastValueFrom(this.apiService.deleteLocation(storeId));
      },
      onSuccess: () => {
        // Invalidar lista de stores
        this.queryClient.invalidateQueries({
          queryKey: locationsQueryKeys.stores(),
        });
      },
    }));
  }

  // ==================== UTILS ====================

  /**
   * Pre-carga los datos de un store en el cache
   * Útil para mejorar UX antes de navegar al detalle
   *
   * @param storeId - ID del store
   *
   * @example
   * ```typescript
   * // Al hacer hover sobre una card:
   * onStoreHover(storeId: number) {
   *   this.queries.prefetchStore(storeId);
   * }
   * ```
   */
  async prefetchStore(storeId: number): Promise<void> {
    await this.queryClient.prefetchQuery({
      queryKey: locationsQueryKeys.storeDetail(storeId),
      queryFn: () =>
        lastValueFrom(this.apiService.getLocationDetail(storeId)),
      staleTime: 1000 * 60 * 2,
    });
  }
}
